import { CONFIG } from '../../../config'
import { IPhoto, IImageStorage, IEnvironment } from '../../..'
import { processPhoto } from './processPhoto'
import { logError } from '../../../logging'
import {
  convertBase64Image,
  getRandomFileName,
  getVideoContentType
} from './imageUtils'

interface ICordovaMediaFile {
  name?: string
  fullPath: string
  type?: string
}

interface ICordovaCaptureError {
  code?: number
  message?: string
}

interface ICordovaCapture {
  captureVideo: (
    success: (mediaFiles: ICordovaMediaFile[]) => void,
    error: (captureError: ICordovaCaptureError) => void,
    options?: {
      limit?: number
      duration?: number
      quality?: number
    }
  ) => void
}

interface ICordovaNavigator extends Navigator {
  device?: {
    capture?: ICordovaCapture
  }
}

export interface ICordovaRecordedVideoFile {
  fileName: string
  filePath: string
  contentType: string
}

function getCDVFileError(method: string, error: any): Error {
  return new Error(
    `${method} failed. Code ` +
      ((typeof error == 'object' && error.code) || 'unknown')
  )
}

function getCordovaImageOptions(): CameraOptions {
  return {
    destinationType: Camera.DestinationType.FILE_URI,
    encodingType: Camera.EncodingType.JPEG,
    targetWidth: CONFIG.imageMaxResolution,
    targetHeight: CONFIG.imageMaxResolution,
    correctOrientation: true,
    cameraDirection: Camera.Direction.BACK
  }
}

function getCaptureErrorCode(name: string): number | undefined {
  if (typeof window === 'undefined') {
    return undefined
  }

  const captureError = (window as any).CaptureError
  return typeof captureError?.[name] === 'number'
    ? captureError[name]
    : undefined
}

function isCancelledCaptureError(error: ICordovaCaptureError): boolean {
  const cancelledCode = getCaptureErrorCode('CAPTURE_NO_MEDIA_FILES')
  if (
    typeof cancelledCode === 'number' &&
    typeof error?.code === 'number'
  ) {
    return error.code === cancelledCode
  }

  return /no media files/i.test(error?.message || '')
}

function isCaptureNotSupportedError(error: ICordovaCaptureError): boolean {
  const notSupportedCode = getCaptureErrorCode('CAPTURE_NOT_SUPPORTED')
  if (
    typeof notSupportedCode === 'number' &&
    typeof error?.code === 'number'
  ) {
    return error.code === notSupportedCode
  }

  return /not supported/i.test(error?.message || '')
}

function getFileNameFromPath(filePath: string): string {
  return filePath.split('?')[0].split('/').slice(-1)[0]
}

function captureVideoFile(): Promise<ICordovaMediaFile | null> {
  return new Promise<ICordovaMediaFile | null>((resolve, reject) => {
    const capture = (navigator as ICordovaNavigator).device?.capture
    if (!capture?.captureVideo) {
      resolve(null)
      return
    }

    capture.captureVideo(
      (mediaFiles) => {
        resolve(mediaFiles?.[0] || null)
      },
      (error) => {
        if (isCancelledCaptureError(error)) {
          resolve(null)
        } else {
          reject(error)
        }
      },
      {
        limit: 1
      }
    )
  })
}



function getCordovaPhotoFile(
  sourceType: number,
  saveToPhotoAlbum: boolean
): Promise<string | null> {
  return new Promise<string | null>((resolve, reject) => {
    navigator.camera.getPicture(
      (imagePath) => {
        resolve(imagePath)
      },
      (error: string) => {
        if (error == 'No Image Selected') {
          resolve(null)
        } else {
          reject(new Error(error))
        }
      },
      {
        ...getCordovaImageOptions(),
        sourceType,
        saveToPhotoAlbum
      }
    )
  })
}

export function getPhotoFileFromCamera(
  saveToPhotoAlbum: boolean
): Promise<string | null> {
  if (!navigator.camera) {
    alert('Take Photo currently supported in the mobile app only.')
    throw new Error('Take Photo currently supported in the mobile app only')
  }
  return getCordovaPhotoFile(Camera.PictureSourceType.CAMERA, saveToPhotoAlbum)
}

export function getPhotoFileFromGallery(): Promise<string | null> {
  if (!navigator.camera) {
    alert('Select from Gallery currently supported in the mobile app only.')
    throw new Error(
      'Select from Gallery currently supported in the mobile app only.'
    )
  }
  return getCordovaPhotoFile(Camera.PictureSourceType.PHOTOLIBRARY, false)
}

function getRecordedVideoFromGetPicture(): Promise<ICordovaRecordedVideoFile | null> {
  return new Promise<ICordovaRecordedVideoFile | null>((resolve, reject) => {
    navigator.camera.getPicture(
      (filePath: string) => {
        if (!filePath) {
          resolve(null)
          return
        }
        const fileName = getFileNameFromPath(filePath)
        resolve({
          fileName,
          filePath,
          contentType: getVideoContentType(fileName)
        })
      },
      (error: string) => {
        if (error == 'No Image Selected') {
          resolve(null)
        } else {
          reject(new Error(error))
        }
      },
      {
        destinationType: Camera.DestinationType.FILE_URI,
        sourceType: Camera.PictureSourceType.CAMERA,
        mediaType: Camera.MediaType.VIDEO
      }
    )
  })
}

export async function getRecordedVideoFile(): Promise<ICordovaRecordedVideoFile | null> {
  const hasCaptureVideo = !!(navigator as ICordovaNavigator).device?.capture
    ?.captureVideo
  const hasGetPicture = !!navigator.camera?.getPicture

  if (!hasCaptureVideo && !hasGetPicture) {
    throw new Error(
      'This version of the mobile app does not support native video capture. Please update the app from the App Store or Play Store.'
    )
  }

  if (hasCaptureVideo) {
    try {
      const mediaFile = await captureVideoFile()
      if (!mediaFile) {
        return null
      }

      const rawPath = mediaFile.fullPath
      const filePath = rawPath.startsWith('file://')
        ? rawPath
        : 'file://' + rawPath
      const fileName = mediaFile.name || getFileNameFromPath(rawPath)
      return {
        fileName,
        filePath,
        contentType: mediaFile.type || getVideoContentType(fileName)
      }
    } catch (error) {
      if (!isCaptureNotSupportedError(error as ICordovaCaptureError)) {
        throw error
      }
      if (!hasGetPicture) {
        throw new Error(
          'This version of the mobile app does not support native video capture. Please update the app from the App Store or Play Store.'
        )
      }
    }
  }

  return getRecordedVideoFromGetPicture()
}

export function getFileData(
  filePath: string,
  contentType = 'image/jpeg'
): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    window.resolveLocalFileSystemURL(
      filePath,
      (entry: any) => {
        console.log('got file entry', entry)
        entry.file(
          (file: any) => {
            console.log('got file')
            const reader = new FileReader()
            reader.onloadend = () => {
              console.log('File read complete. Returning Blob...')
              const data = new Blob([new Uint8Array(reader.result as any)], {
                type: file.type || contentType
              })
              resolve(data)
            }
            reader.onerror = () => {
              reject(new Error('FileReader.read() failed.'))
            }
            reader.readAsArrayBuffer(file)
          },
          (error: any) => {
            reject(getCDVFileError('file()', error))
          }
        )
      },
      (error: any) => {
        reject(getCDVFileError('resolveLocalFileSystemURL()', error))
      }
    )
  })
}

export function getPhotoData(filePath: string): Promise<Blob> {
  return getFileData(filePath, 'image/jpeg')
}

export async function takeFromCamera(
  addPhotosToGallery: boolean,
  imageStorage: IImageStorage,
  env: IEnvironment
): Promise<IPhoto | null> {
  try {
    const photoFile = await getPhotoFileFromCamera(addPhotosToGallery)
    if (photoFile) {
      let photo: IPhoto
      if (env.platform == 'cordova' && env.os == 'browser') {
        const photoData = convertBase64Image(photoFile)
        console.log('got photo data', photoData.fileType)
        const randomString = getRandomFileName()
        let ext = ''
        if (photoData.fileType) ext = '.' + photoData.fileType.ext
        const fileName = randomString + ext
        photo = await processPhoto(photoData.file, fileName, imageStorage)
      } else {
        // Extract filename from file://aaa/bbb/c.jpg?12345
        let fileName = photoFile.split('?')[0].split('/').slice(-1)[0]
        const ext = fileName.substring(
          fileName.lastIndexOf('.'),
          fileName.length
        )
        // add a random string to the file name to avoid duplicate file name problem
        const randomString = getRandomFileName()
        fileName =
          fileName.substring(0, fileName.lastIndexOf('.')) +
          '_' +
          randomString +
          ext
        const photoData = await getPhotoData(photoFile)
        console.log('got photo data', photoData)
        photo = await processPhoto(photoData, fileName, imageStorage)
      }
      console.log('photo processed')
      photo.attachmentType = 'photo'
      photo.source = 'camera'
      return photo
    }
  } catch (e) {
    logError(e, 'Error while processing image from camera')
  }
  return null
}
